import { useMultiColumnAutoCompleteStore } from '@modules/common/components/MultiColumnAutoComplete/hooks/useMultiColumnAutoCompleteStore';
import { MultiColumnAutoCompleteActionType } from '@modules/common/components/MultiColumnAutoComplete/hooks/useMultiColumnAutoCompleteStore/multiColumnAutoCompleteActionType';
import { MultiColumnAutoCompleteState } from '@modules/common/components/MultiColumnAutoComplete/hooks/useMultiColumnAutoCompleteStore/types';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteCloseReason,
} from '@mui/material';
import { KeyboardEvent, SyntheticEvent } from 'react';
import { UUID } from 'v2/core/entities/uuid';
import { shallow } from 'zustand/shallow';

interface useKeyboardNavigationParams<T> {
  options: readonly T[];
  handleOpen: (event: KeyboardEvent) => void;
  handleClose: (event: SyntheticEvent, reason: AutocompleteCloseReason) => void;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    value: T | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T> | undefined,
  ) => void;
}

export function useKeyboardNavigation<T extends { id: UUID }>({
  options,
  onChange,
  handleOpen,
  handleClose,
}: useKeyboardNavigationParams<T>) {
  const dispatch = useMultiColumnAutoCompleteStore(({ dispatch }) => dispatch);

  const {
    activeRowIndex,
    activeOptionIndex,
    activeColumnIndex,
    optionsLimitByRowColumn,
    groupsByRowColumn,
    optionIDByRowColumnOptionIndex,
  } = useMultiColumnAutoCompleteStore(
    ({
      activeRowIndex,
      activeOptionIndex,
      activeColumnIndex,
      columnOptionLimitByRowID: optionsLimitByRowColumn,
      groupsByRowColumn,
      optionIDByRowColumnOptionIndex,
    }) => {
      return {
        activeOptionIndex,
        activeColumnIndex,
        activeRowIndex,
        optionsLimitByRowColumn,
        groupsByRowColumn,
        optionIDByRowColumnOptionIndex,
      };
    },
    shallow,
  );

  const isSingleColumnDropdown = groupsByRowColumn.every(
    (rowGroup) => rowGroup.length < 2,
  );

  return (event: KeyboardEvent) => {
    const payload: Partial<MultiColumnAutoCompleteState> = {};

    switch (event.key) {
      case 'Enter': {
        const optionID =
          optionIDByRowColumnOptionIndex[activeRowIndex]?.[activeColumnIndex]?.[
            activeOptionIndex
          ];
        const option =
          (optionID ? options.find(({ id }) => id === optionID) : null) ?? null;

        onChange?.(event, option, 'selectOption');
        return;
      }

      case 'ArrowLeft': {
        if (isSingleColumnDropdown) {
          return;
        }

        event.preventDefault();
        handleOpen(event);

        payload.activeColumnIndex = Math.abs(
          (activeColumnIndex - 1) % groupsByRowColumn[activeRowIndex]?.length,
        );
        payload.activeOptionIndex = Math.min(
          activeOptionIndex,
          optionsLimitByRowColumn[activeRowIndex][payload.activeColumnIndex] -
            1,
        );

        break;
      }

      case 'ArrowRight': {
        if (isSingleColumnDropdown) {
          return;
        }

        event.preventDefault();
        handleOpen(event);

        payload.activeColumnIndex =
          (activeColumnIndex + 1) % groupsByRowColumn[activeRowIndex]?.length;
        payload.activeOptionIndex = Math.min(
          activeOptionIndex,
          optionsLimitByRowColumn[activeRowIndex][payload.activeColumnIndex] -
            1,
        );

        break;
      }

      case 'ArrowDown': {
        event.preventDefault();
        handleOpen(event);

        const newOptionIndex = activeOptionIndex + 1;
        const isNewOptionIdxOutOfRange =
          newOptionIndex >
          optionsLimitByRowColumn[activeRowIndex][activeColumnIndex] - 1;

        if (isNewOptionIdxOutOfRange) {
          const nextRowIndex = activeRowIndex + 1;
          const canMoveToNextRow = nextRowIndex < groupsByRowColumn.length;

          if (canMoveToNextRow) {
            payload.activeRowIndex = nextRowIndex;
            payload.activeColumnIndex =
              activeColumnIndex < groupsByRowColumn[nextRowIndex].length
                ? activeColumnIndex
                : 0;
            payload.activeOptionIndex = 0;
          } else {
            const firstRowIndex = 0;

            payload.activeRowIndex = firstRowIndex;
            payload.activeColumnIndex =
              activeColumnIndex < groupsByRowColumn[firstRowIndex].length
                ? activeColumnIndex
                : 0;
            payload.activeOptionIndex = 0;
          }
        } else {
          payload.activeOptionIndex = newOptionIndex;
        }

        break;
      }

      case 'ArrowUp': {
        event.preventDefault();
        handleOpen(event);

        const newOptionIndex = activeOptionIndex - 1;
        const isNewOptionIdxOutOfRange = newOptionIndex < 0;

        if (isNewOptionIdxOutOfRange) {
          const prevRowIndex = activeRowIndex - 1;
          const canMoveToPreviousRow = prevRowIndex >= 0;

          if (canMoveToPreviousRow) {
            payload.activeRowIndex = prevRowIndex;
            payload.activeColumnIndex =
              activeColumnIndex < groupsByRowColumn[prevRowIndex].length
                ? activeColumnIndex
                : 0;
            payload.activeOptionIndex =
              optionsLimitByRowColumn[prevRowIndex][payload.activeColumnIndex] -
              1;
          } else {
            const lastRowIndex = groupsByRowColumn.length - 1;

            payload.activeRowIndex = lastRowIndex;
            payload.activeColumnIndex =
              activeColumnIndex < groupsByRowColumn[lastRowIndex].length
                ? activeColumnIndex
                : 0;
            payload.activeOptionIndex =
              optionsLimitByRowColumn[lastRowIndex][activeColumnIndex] - 1;
          }
        } else {
          payload.activeOptionIndex = newOptionIndex;
        }

        break;
      }

      case 'Escape': {
        event.preventDefault();
        event.stopPropagation();
        handleClose(event, 'escape');
      }
    }

    dispatch({
      type: MultiColumnAutoCompleteActionType.SET,
      payload,
    });
  };
}
